  import { defineComponent, type PropType } from "vue";
  import EtablissementSelect from '@/extend/controls/etablissement/EtablissementSelect';
  import TransactionAmount from '@/extend/controls/transaction/TransactionAmount';
  import TransactionReference from '@/extend/controls/transaction/TransactionReference';
  import Transaction from '@/extend/classes/Transaction';
  import { FieldStatus, EditFieldTheme, SectionStatus } from '@/framework/base/components/FieldInfo';
  import { Alerts } from '@/framework/base/helpers/Alerts'; 
  import { storeTransaction } from '@/generated/stores';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import { Extended } from '@/framework/base/classes/Schema';
  import ButtonPlus from '@/framework/base/components/ButtonPlus.vue';
  import Route from '@/generated/classes/Route';
  import { Fiches, Sections } from '@/generated/classes/AppSchema';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  export default defineComponent({
    components: {
      SectionHeader,
      LoaderButton,
      ButtonPlus,
      EtablissementSelect,
      TransactionAmount,
      TransactionReference,
    },

    props: {
      transaction: {
        type: Object as PropType<Transaction>,
        required: true
      },
      showButton: {
        type: Boolean,
      },
      autoSave: {
        type: Boolean,
      },
      fieldOnly: {
        type: Boolean,
      },
      extended: {
        type: Boolean,
      },
      autoClear: {
        type: Boolean,
      },
      dialog: {
        type: Boolean,
      },
      closeSection: {
        type: Boolean,
      },
      showMissing: {
        type: Boolean,
      },
      moduleId: {
        type: Number,
      },
      theme: {
        type: Object as PropType<EditFieldTheme>,
      },
      fiche: {
        type: String,
      },
      fromSection: {
        type: String,
      },
      requiredAttributs: {
        type: Array as PropType<string[]>,
      },
      toggleResetEtablissementSelect: {
        type: Boolean
      },
    },

    beforeUnmount (): void {
      if (!this.closeSection) {
        this.beforeClose();
      }
    },

    data() {
      return {
    toggleEtablissementId: false,
        etablissementIdStatus: new FieldStatus(false, false, 0),
        strEtablissementId: '',
        amountStatus: new FieldStatus(false, false, 0),
        strAmount: '',
        referenceStatus: new FieldStatus(false, false, 0),
        strReference: '',
        refTransaction: new Transaction,
        cloneTransaction: new Transaction,
      transactionSaving: false,
        saving: false,
        restoring: false,
        inDialog: false,
        updated: false,
        error: false,
        saveLoading: false,
        saveFailed: false,
        allowClearRef: true,
        objType: ObjTypes.TRANSACTION,
        status: new SectionStatus(false, false, false),
        autoClose: true,
        closing: false
      }
    },
    computed: {
      isExtended (): boolean {
        return this.extended;
      },

      exist (): boolean {
        return this.editTransaction.id != 0
      },

      extendIcon (): string {
        return this.extended ? 'fas fa-circle-minus' : 'fas fa-circle-plus';
      },

      saveButton (): boolean {
        return (this.showButton == undefined || this.showButton == true) && !this.allReadOnly;
      },

      editTransaction: {
        get(): Transaction {
          if (this.closeSection) {
            this.beforeClose();
          } else {
            if (this.transaction != undefined) {
              if (this.transaction.id == 0) {
                Alerts.debug('CrudTransaction : les objets passés en paramètre doivent être issus du store (id <> 0)');
              }
              if (this.cloneTransaction.id != this.transaction.id && !this.saving) {
                this.handleTransactionChange();
              }
            }
          }
          return this.refTransaction;
        },
        set(value: Transaction) {
          this.refTransaction = value;
        }
      },

      canCreate (): boolean {
        return this.refTransaction.id > 0;
      },

      canDelete (): boolean {
        return this.refTransaction.canDelete && this.refTransaction.id > 0;
      },

      updatedAndValid (): boolean {
        return this.updated && !this.error;
      },

      clearAuto (): boolean {
        return (this.autoClear == undefined || this.autoClear == true) && this.allowClearRef;
      },

    resetEtablissementId (): boolean {
      return this.toggleEtablissementId
    },

      etablissementId: {
        get(): number {
          return this.editTransaction.etablissementId;
        },
        set(value: number) {
          this.editTransaction.etablissementId = value;
          this.fieldUpdated('ETABLISSEMENT_ID', value);
        }
      },


      amount: {
        get(): string {
          return this.strAmount;
        },
        set(value: string) {
          const amount = parseFloat(value);
          if (amount.toString() == value) {
            this.editTransaction.amount = amount;
            this.fieldUpdated('AMOUNT', value);
          }
          this.strAmount = value;
        }
      },


      reference: {
        get(): string {
          return this.editTransaction.reference;
        },
        set(value: string) {
          this.editTransaction.reference = value;
          this.fieldUpdated('REFERENCE', value);
        }
      },


      allReadOnly (): boolean {
        const res = this.transaction.canUpdate && (
        (this.isDisplayed('ETABLISSEMENT_ID') && !this.readOnly('ETABLISSEMENT_ID')) ||
        (this.isDisplayed('AMOUNT') && !this.readOnly('AMOUNT')) ||
        (this.isDisplayed('REFERENCE') && !this.readOnly('REFERENCE')));
        return !res;
      },

      fields (): string[] {
        return ['ETABLISSEMENT_ID', 'AMOUNT', 'REFERENCE'];
      },
    },

    methods: {
      handleTransactionChange() : void {
        // Edited object changed
        this.saveFailed = false;
        if (this.autoSave && this.updatedAndValid) {
          if (!this.inDialog && this.checkFields()) {
            // Dialogue to save or cancel changes
            this.inDialog = true;
            this.dialogSaveOrCancel().then(() => {
              this.createRef(this.transaction);
              this.inDialog = false;
                  })
          } else if (!this.restoring && !this.inDialog) {
            this.restoreEdited();
            this.createRef(this.transaction);
          }
        } else if (!this.inDialog) {
          if (this.cloneTransaction.id < 0) {
            this.clearRef();
          } else if (this.updated) {
            this.restoreEdited();
          }
          this.createRef(this.transaction);
        }
      },
      beforeClose (): void {
        if (!this.closing) {
          this.closing = true;
          if (this.autoSave && this.updatedAndValid && this.checkFields()) {
            this.$emit('saveOrCancel');
            this.dialogSaveOrCancel();
          } else {
            if (this.autoSave && this.updated) {
              this.restoreEdited();
            }
            this.$emit('cancelled');
          }
          this.clearRef();
          }
      },

      setExtended (extended: Extended): void {
         this.$emit('extended', extended);
      },

      // eslint-disable-next-line
      isDisplayed (value: string): boolean {
        return true;
      },

      // eslint-disable-next-line
      required (value: string): boolean {
        return false;
      },

      // eslint-disable-next-line
      readOnly (value: string): boolean {
        return false;
      },

      save (): void {
        if (event != undefined) {
          event.stopPropagation();
        }
        this.saveLoading = true;
        this.saveFailed = false;
        if (this.checkFields()) {
          this.saveObj();
        } else {
        this.saveLoading = false;
          this.saveFailed = true;
        }
      },

      setUpdated (): void {
        const updated = (this.etablissementIdStatus != undefined && this.etablissementIdStatus.updated) ||
          (this.amountStatus != undefined && this.amountStatus.updated) ||
          (this.referenceStatus != undefined && this.referenceStatus.updated);
        if (updated != this.updated) {
          this.updated = updated;
          this.status.updated = updated;
          this.status.missing = this.hasMissing();
          if (!this.closing) {
            this.$emit('status', this.status);
          }
          this.$emit('updated', this.transaction);
        }
      },

      setError (): void {
        const error = this.etablissementIdStatus.error ||
        this.amountStatus.error ||
        this.referenceStatus.error;
        if (error != this.error) {
          if (!error) {
            this.saveFailed = false;
          }
          this.error = error;
          this.status.error = error;
          if (!this.closing) {
            this.$emit('status', this.status);
          }
          this.$emit('updated', this.transaction);
        }
      },

      // Clear non saved new objects when leaving
      clearRef (): void {
        if (this.clearAuto && this.refTransaction.id < 0) {
          storeTransaction.mTransactionDeleted(this.refTransaction.id);
        }
      },

      // Dialog to save or ignore changes when loosing focus or changing edited item
      dialogSaveOrCancel (): Promise<boolean> {
        return new Promise((resolve) => {
          if (!this.error) {
            Alerts.saveOrCancel()
              .then((result: boolean) => {
                if (result) {
                  this.saveObj();
                } else {
                  if (this.refTransaction.id <= 0) {
                    this.deleteObj();
                  } else {
                    this.restoreEdited();
                  }
                  this.$emit('cancelled');
                }
                resolve(result);
              })
          } else {
            this.restoreEdited();
          }
        })
      },

      // Save object and subobjects referenced by foreignClass keys
      saveObj (): void {
        this.saving = true;
        if (
          this.saveTransaction()
          ) {
          this.saving = false;
          this.saveLoading = false;
          this.transactionSaving = false;
          this.createRef(this.refTransaction);
          this.$emit('updated', this.refTransaction);
          if (this.transaction.id > 0) {
            this.$emit('saved', this.refTransaction);
          } else {
            this.updated = true;
          }
          if (this.autoClose) {
            this.saveFields();
            this.$emit('closeDialog');
          }
        } else {
          if (this.refTransaction.failed) {
          this.saveLoading = false;
            this.transactionSaving = false;
            this.refTransaction.failed = false;
          }
        }
      },

      saveTransaction (): boolean {
        if (!this.refTransaction.compareTransaction(this.cloneTransaction)) {
          if (!this.transactionSaving) {
            this.transactionSaving = true;
          if (this.refTransaction.id > 0) {
            storeTransaction.updateTransaction(this.refTransaction)
              .then((data: Transaction) => {
                if (!data.failed) {
                  this.refTransaction = storeTransaction.getTransaction(data.id);
                  this.cloneTransaction = data.cloneTransaction();
                } else {
                  this.refTransaction.failed = true;
                }
                this.saveObj();
              })
              .catch(() => {
                this.restoreEdited();
                this.transactionSaving = false;
                this.saveLoading = false;
              })
          }
            if (this.refTransaction.id <= 0) {
              storeTransaction.createTransaction(this.refTransaction)
                .then((data: Transaction) => {
                  if (!data.failed) {
                    this.refTransaction = storeTransaction.getTransaction(data.id);
                    this.cloneTransaction = data.cloneTransaction();
                  } else {
                this.refTransaction.failed = true;
                  }
                  this.saveObj();
                })
                .catch(() => {
                  this.restoreEdited();
                    this.transactionSaving = false;
                })
            }
          }
          return false;
        } else {
          return true;
        }
      },

      // Delete object
      deleteObj (): void {
        if (this.refTransaction.id <= 0) {
          storeTransaction.mTransactionDeleted(this.refTransaction.id);
        }
      },

      statusEtablissementId (value: FieldStatus): void {
        this.etablissementIdStatus = value;
        this.setUpdated();
        this.setError();
      },
      statusAmount (value: FieldStatus): void {
        this.amountStatus = value;
        this.setUpdated();
        this.setError();
      },
      statusReference (value: FieldStatus): void {
        this.referenceStatus = value;
        this.setUpdated();
        this.setError();
      },

      deleteTransaction(): void {
        this.$emit('deleteTransaction',  this.transaction);
      },

    addTransaction(): void {
      this.$emit('addTransaction',  this.transaction);
    },

      // Restore object
      restoreEdited (): void {
        if (this.cloneTransaction != undefined) {
          this.restoring = true;
          this.refTransaction.etablissementId = this.cloneTransaction.etablissementId;
          this.refTransaction.amount = this.cloneTransaction.amount;
          this.refTransaction.reference = this.cloneTransaction.reference;
          this.restoring = false;
        }
      },


    // Clone and clear edited
      createRef (value: Transaction): void {
        this.clearRef();
        this.editTransaction = value;
        this.cloneTransaction = value.cloneTransaction();
        this.etablissementIdStatus.clear(value.id);
        this.amountStatus.clear(value.id);
        this.referenceStatus.clear(value.id);
        this.status.clear();
        this.strAmount = this.editTransaction.amount.toString();
      },

    checkFields (): boolean {
        // @ts-expect-error : generated method
        if (this.isDisplayed('ETABLISSEMENT_ID') && this.$refs.ETABLISSEMENT_ID) this.$refs.ETABLISSEMENT_ID.touch();
        // @ts-expect-error : generated method
        if (this.isDisplayed('AMOUNT') && this.$refs.AMOUNT) this.$refs.AMOUNT.touch();
        // @ts-expect-error : generated method
        if (this.isDisplayed('REFERENCE') && this.$refs.REFERENCE) this.$refs.REFERENCE.touch();
        return !this.error;
      },

      missing (value: string): boolean {
        return this.hasMissing(value);
      },

      hasMissing (value = ''): boolean {
        let missing = false;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'ETABLISSEMENT_ID') && this.isDisplayed('ETABLISSEMENT_ID') && this.$refs.ETABLISSEMENT_ID && this.$refs.ETABLISSEMENT_ID.missing()) missing = true;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'AMOUNT') && this.isDisplayed('AMOUNT') && this.$refs.AMOUNT && this.$refs.AMOUNT.missing()) missing = true;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'REFERENCE') && this.isDisplayed('REFERENCE') && this.$refs.REFERENCE && this.$refs.REFERENCE.missing()) missing = true;
        return missing;
      },

      saveFields (): void {
        // @ts-expect-error : generated method
        if (this.isDisplayed('ETABLISSEMENT_ID') && this.$refs.ETABLISSEMENT_ID) this.$refs.ETABLISSEMENT_ID.save();
        // @ts-expect-error : generated method
        if (this.isDisplayed('AMOUNT') && this.$refs.AMOUNT) this.$refs.AMOUNT.save();
        // @ts-expect-error : generated method
        if (this.isDisplayed('REFERENCE') && this.$refs.REFERENCE) this.$refs.REFERENCE.save();
      },

      getValue (field: string): string {
         if (field == 'ETABLISSEMENT_ID') return this.etablissementId.toString();
        else  if (field == 'AMOUNT') return this.amount.toString();
        else  if (field == 'REFERENCE') return this.reference.toString();
        else return '';
      },

    // eslint-disable-next-line
    fieldUpdated (field: string, value: unknown): void {
      // To implement
    },

      setFocus (route: Route): boolean {
        let res = false;
        let first = '';
        const attribut = route ? route.attribut : '';
        this.fields.forEach(field => {
          // @ts-expect-error : generated method
          if (!res && this.isDisplayed(field) && !this.readOnly(field) && this.$refs[field].setFocus) {
            if (attribut == field) {
              // @ts-expect-error : generated method
              this.$refs[attribut].setFocus();
              res = true;
            } else if (first == '') {
              first = field;
            }
          }
        })
        // @ts-expect-error : generated method
        if (!res && first != '' && this.$refs[first] && this.$refs[first].setFocus) {
          // @ts-expect-error : generated method
          this.$refs[first].setFocus();
          res = true;
        }
        return res
      },

      closeDialog(): void {
        this.$emit('closeDialog')
      }
    },
  })
